import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useVehicles, VehicleFormData } from "./useVehicles";
import { useCompany } from "./useCompany";
import analytics from "@/lib/segment";
import { generateAndUploadVehicleImage } from "@/lib/generateVehicleImage";
import { deleteStorageFiles } from "@/lib/storageCleanup";
import {
  parseCSV,
  parseExpenseRow,
  ParsedExpense,
} from "@/lib/expenseImport";
import { validateExpensesCSV } from "@/lib/csvValidation";

export interface Expense {
  id: string;
  user_id: string;
  company_id?: string | null;
  vehicle_id?: string | null;
  amount: number;
  expense_date: string;
  description?: string | null;
  expense_name?: string | null;
  expense_type?: string | null;
  tax_category?: string | null;
  mileage?: number | null;
  paid_by?: string | null;
  paid_to?: string | null;
  notes?: string | null;
  loan_payment_type?: string | null;
  receipt_url?: string | null;
  receipt_urls?: string[] | null;
  created_at: string;
}

export interface ExpenseFormData {
  vehicle_id?: string | null;
  amount: number;
  expense_date: string;
  description?: string;
  expense_name?: string;
  expense_type?: string;
  tax_category?: string;
  mileage?: number;
  paid_by?: string;
  paid_to?: string;
  notes?: string;
  receipt_urls?: string[];
}

export const INITIAL_EXPENSE_TYPES = ["Car Purchase", "Initial Repairs", "Initial Fees"];

export const DEFAULT_EXPENSE_TYPES = [
  "Car Purchase",
  "Initial Repairs",
  "Initial Fees",
  "Cleaning",
  "Rental Operations",
  "Maintenance",
  "Tolls",
  "Parking",
  "Mileage",
  "Administration",
  "Fees",
  "Loans",
  "Insurance",
  "Software",
  "Education",
  "Extras",
  "Equipment",
  "Unexpected",
  "Other",
];

export const DEFAULT_PAID_BY_OPTIONS = ["Personal", "LLC"];

export const DEFAULT_TAX_CATEGORIES = [
  "Advertising",
  "Car and truck expenses",
  "Commissions and fees",
  "Contract labor",
  "Depletion",
  "Depreciation and section 179",
  "Employee benefit programs",
  "Vehicle insurance",
  "Interest",
  "Legal and professional services",
  "Office expense",
  "Pension and profit-sharing",
  "Rent or lease",
  "Repairs and maintenance",
  "Supplies",
  "Taxes and licenses",
  "Travel and meals",
  "Utilities",
  "Wages",
  "Other expenses - Training",
  "Other expenses - Software",
  "Other expenses - Bank Fees",
  "Other expenses - Tools and Equipment",
  "Other expenses - Parking and Tolls",
  "Other expenses - Merchant Processing",
];

export const useExpenses = () => {
  const queryClient = useQueryClient();
  const { vehicles } = useVehicles();
  const { company, members } = useCompany();

  // Fetch expenses (filter by company or user, same pattern as trips)
  const { data: expenses = [], isLoading } = useQuery({
    queryKey: ["expenses", company?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [] as Expense[];

      let query = supabase
        .from("expenses")
        .select("*")
        .order("expense_date", { ascending: false })
        .order("created_at", { ascending: false });

      if (company?.id) {
        query = query.eq("company_id", company.id);
      } else {
        query = query.eq("user_id", user.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Expense[];
    },
  });

  // Fetch custom expense type options
  const { data: customExpenseTypes = [] } = useQuery({
    queryKey: ["expense-type-options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expense_type_options")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data.map((opt) => opt.option_value);
    },
  });

  // Fetch custom paid_by options
  const { data: customPaidByOptions = [] } = useQuery({
    queryKey: ["paid-by-options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("paid_by_options")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data.map((opt) => opt.option_value);
    },
  });

  // Fetch custom tax category options
  const { data: customTaxCategories = [] } = useQuery({
    queryKey: ["tax-category-options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tax_category_options")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data.map((opt: any) => opt.option_value);
    },
  });

  // Combined expense types (defaults + custom)
  const expenseTypes = [...DEFAULT_EXPENSE_TYPES, ...customExpenseTypes];

  // Combined paid_by options (defaults + custom + company member emails)
  const memberEmails = members
    ?.filter((m) => m.profiles?.email)
    .map((m) => m.profiles!.email!) || [];
  const paidByOptions = [...DEFAULT_PAID_BY_OPTIONS, ...memberEmails, ...customPaidByOptions];

  // Combined tax categories (defaults + custom)
  const taxCategories = [...DEFAULT_TAX_CATEGORIES, ...customTaxCategories];

  // Add expense
  const addExpense = useMutation({
    mutationFn: async (expenseData: ExpenseFormData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("expenses")
        .insert({
          ...expenseData,
          user_id: user.id,
          company_id: company?.id || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      analytics.track("Expense record added", {
        expense_type: data.expense_type,
        amount: data.amount,
      });
      toast.success("Expense added");
    },
    onError: (error) => {
      toast.error("Failed to add expense: " + error.message);
    },
  });

  // Update expense
  const updateExpense = useMutation({
    mutationFn: async ({ id, ...expenseData }: Partial<ExpenseFormData> & { id: string }) => {
      const { data, error } = await supabase
        .from("expenses")
        .update(expenseData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
    },
    onError: (error) => {
      toast.error("Failed to update expense: " + error.message);
    },
  });

  // Delete expense
  const deleteExpense = useMutation({
    mutationFn: async (id: string) => {
      // Fetch the expense first to get receipt URLs
      const { data: expense } = await supabase
        .from("expenses")
        .select("receipt_urls")
        .eq("id", id)
        .single();

      // Delete associated receipt files from storage
      if (expense?.receipt_urls && Array.isArray(expense.receipt_urls)) {
        await deleteStorageFiles(expense.receipt_urls as string[], "expense-receipts");
      }

      const { error } = await supabase
        .from("expenses")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      toast.success("Expense deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete expense: " + error.message);
    },
  });

  // Add custom expense type
  const addExpenseType = useMutation({
    mutationFn: async (optionValue: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("expense_type_options")
        .insert({
          option_value: optionValue,
          user_id: user.id,
          company_id: company?.id || null,
          sort_order: customExpenseTypes.length,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expense-type-options"] });
      toast.success("Expense type added");
    },
    onError: (error) => {
      toast.error("Failed to add expense type: " + error.message);
    },
  });

  // Add custom paid_by option
  const addPaidByOption = useMutation({
    mutationFn: async (optionValue: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("paid_by_options")
        .insert({
          option_value: optionValue,
          user_id: user.id,
          company_id: company?.id || null,
          sort_order: customPaidByOptions.length,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["paid-by-options"] });
      toast.success("Paid by option added");
    },
    onError: (error) => {
      toast.error("Failed to add paid by option: " + error.message);
    },
  });

  // Add custom tax category option
  const addTaxCategory = useMutation({
    mutationFn: async (optionValue: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("tax_category_options")
        .insert({
          option_value: optionValue,
          user_id: user.id,
          company_id: company?.id || null,
          sort_order: customTaxCategories.length,
        } as any)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tax-category-options"] });
      toast.success("Tax category added");
    },
    onError: (error) => {
      toast.error("Failed to add tax category: " + error.message);
    },
  });

  // Import expenses from CSV
  const importExpenses = useMutation({
    mutationFn: async (params: { file: File; vehicleLimit?: number | null }): Promise<{ success: boolean; imported: number; skippedVehicles: number; errors: string[] }> => {
      const { file, vehicleLimit } = params;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const content = await file.text();
      const { headers, rows } = parseCSV(content);

      // Validate headers and check for wrong file type
      const validation = validateExpensesCSV(headers);
      if (!validation.valid) {
        return {
          success: false,
          imported: 0,
          skippedVehicles: 0,
          errors: [validation.message],
        };
      }

      // Get fresh vehicles list
      const { data: currentVehicles } = await supabase
        .from("vehicles")
        .select("*")
        .order("created_at", { ascending: false });

      let vehiclesList = currentVehicles || [];
      const currentVehicleCount = vehiclesList.length;
      const errors: string[] = [];

      // First pass: parse all rows to collect unique new car names
      const parsedRows: { parsed: ParsedExpense; rowIndex: number }[] = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (row.length <= 1 && !row[0]) continue;
        try {
          const parsed = parseExpenseRow(headers, row, vehiclesList, expenseTypes, paidByOptions);
          if (parsed.data.amount === 0 && !parsed.data.mileage) {
            errors.push(`Row ${i + 2}: Either Amount or Miles Driven must be provided.`);
            continue;
          }
          parsedRows.push({ parsed, rowIndex: i });
        } catch (err) {
          errors.push(`Row ${i + 2}: Failed to parse row.`);
        }
      }

      // Collect unique new car names (excluding "Operations")
      const newCarNames = new Set<string>();
      for (const { parsed } of parsedRows) {
        if (parsed.needsNewVehicle && parsed.carValue && parsed.carValue.toLowerCase() !== "operations") {
          newCarNames.add(parsed.carValue);
        }
      }

      // Sort alphabetically and enforce vehicle limit
      const sortedNewCarNames = Array.from(newCarNames).sort((a, b) => a.localeCompare(b));
      let allowedNewVehicles = sortedNewCarNames.length;
      let skippedVehicles = 0;

      if (vehicleLimit !== null && vehicleLimit !== undefined) {
        const remainingSlots = Math.max(0, vehicleLimit - currentVehicleCount);
        allowedNewVehicles = Math.min(sortedNewCarNames.length, remainingSlots);
        skippedVehicles = sortedNewCarNames.length - allowedNewVehicles;
      }

      const allowedNewCarNames = new Set(sortedNewCarNames.slice(0, allowedNewVehicles));

      // Second pass: create allowed vehicles and build final expense list
      const expensesToInsert: ParsedExpense[] = [];
      for (const { parsed, rowIndex } of parsedRows) {
        if (parsed.needsNewVehicle && parsed.carValue && parsed.carValue.toLowerCase() !== "operations") {
          if (!allowedNewCarNames.has(parsed.carValue)) {
            // Skip this expense — vehicle exceeds plan limit
            continue;
          }

          // Check if we already created this vehicle in a previous row
          const alreadyCreated = vehiclesList.find(
            (v) => v.name?.toLowerCase() === parsed.carValue.toLowerCase()
          );

          if (alreadyCreated) {
            parsed.data.vehicle_id = alreadyCreated.id;
            parsed.matchedVehicleId = alreadyCreated.id;
          } else {
            const { data: newVehicle, error: vehicleError } = await supabase
              .from("vehicles")
              .insert({
                name: parsed.carValue,
                year: new Date().getFullYear(),
                make: "Unknown",
                model: "Unknown",
                user_id: user.id,
                company_id: company?.id || null,
              })
              .select()
              .single();

            if (vehicleError) {
              errors.push(`Row ${rowIndex + 2}: Failed to create vehicle "${parsed.carValue}".`);
            } else if (newVehicle) {
              vehiclesList = [...vehiclesList, newVehicle];
              parsed.data.vehicle_id = newVehicle.id;
              parsed.matchedVehicleId = newVehicle.id;
              
              // Fire-and-forget: generate AI vehicle image
              generateAndUploadVehicleImage({
                vehicleId: newVehicle.id,
                make: newVehicle.make || "Unknown",
                model: newVehicle.model || "Unknown",
                year: newVehicle.year,
                nickname: parsed.carValue,
              });
            }
          }
        }

        expensesToInsert.push(parsed);
      }

      if (expensesToInsert.length === 0) {
        return {
          success: false,
          imported: 0,
          skippedVehicles,
          errors: errors.length > 0 ? errors : ["No valid expenses found in CSV."],
        };
      }

      // Insert all expenses
      const { error: insertError } = await supabase
        .from("expenses")
        .insert(
          expensesToInsert.map((e) => ({
            ...e.data,
            user_id: user.id,
            company_id: company?.id || null,
          }))
        );

      if (insertError) {
        return {
          success: false,
          imported: 0,
          skippedVehicles,
          errors: [`Failed to insert expenses: ${insertError.message}`],
        };
      }

      return {
        success: true,
        imported: expensesToInsert.length,
        skippedVehicles,
        errors,
      };
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ["expenses"] });
        queryClient.invalidateQueries({ queryKey: ["vehicles"] });
        
        // Track expenses import
        analytics.track("Expenses CSV imported", {
          row_count: result.imported,
          skipped_vehicles: result.skippedVehicles,
        });
        
        let message = `Imported ${result.imported} expenses`;
        if (result.skippedVehicles > 0) {
          message += `. ${result.skippedVehicles} vehicle(s) skipped due to plan limits.`;
        }
        toast.success(message);
      }
    },
    onError: (error) => {
      toast.error("Failed to import expenses: " + error.message);
    },
  });

  // Clear all expenses
  const clearAllExpenses = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Fetch all expenses to collect receipt URLs before deleting
      let fetchQuery = supabase.from("expenses").select("receipt_urls");
      if (company?.id) {
        fetchQuery = fetchQuery.eq("company_id", company.id);
      } else {
        fetchQuery = fetchQuery.eq("user_id", user.id);
      }
      const { data: allExpenses } = await fetchQuery;

      // Collect all receipt URLs and delete from storage
      if (allExpenses) {
        const allUrls = allExpenses.flatMap(
          (e) => (Array.isArray(e.receipt_urls) ? e.receipt_urls : []) as string[]
        );
        if (allUrls.length > 0) {
          await deleteStorageFiles(allUrls, "expense-receipts");
        }
      }

      let query = supabase.from("expenses").delete();
      if (company?.id) {
        query = query.eq("company_id", company.id);
      } else {
        query = query.eq("user_id", user.id);
      }

      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      
      analytics.track("All expenses cleared");
      
      toast.success("All expenses have been cleared");
    },
    onError: (error) => {
      toast.error("Failed to clear expenses: " + error.message);
    },
  });

  return {
    expenses,
    isLoading,
    vehicles: vehicles || [],
    expenseTypes,
    paidByOptions,
    taxCategories,
    addExpense,
    updateExpense,
    deleteExpense,
    addExpenseType,
    addPaidByOption,
    addTaxCategory,
    importExpenses,
    clearAllExpenses,
  };
};
